import { z } from 'zod';
import { isCalendarDateIso, todayUtcIso } from '../utils/calendar-date.js';
import { isoDateTimeSchema, uuidSchema } from './common.js';
import { isoDateSchema } from './subscription.js';
export const paymentStatusEnum = z.enum([
    'pending',
    'paid',
    'failed',
    'refunded',
]);
export const paymentMethodEnum = z.enum([
    'manual_invoice',
    'online_card',
    'online_sbp',
]);
/**
 * Payment record (admin-arch customers.payments / PRD §6.5).
 *
 * Currency stored as kopecks (bigint) per ADR-010 (CEO decision 2026-05-15).
 * Format to roubles on UI/PDF boundary via formatRub(kop) = (kop/100).toFixed(2).
 * Parse user input to kop via parseRub(input) = Math.round(parseFloat(input) * 100).
 * JS `number` is safe up to 9 квадриллионов копеек — никогда не упрёмся в MAX_SAFE_INTEGER.
 *
 * Wave 2.20 BE-2: extended with `invoice_number` (optional admin bookkeeping
 * label) and `notes` (the routing prefix written by the lifecycle service
 * for prorate / refund placeholders — kept on the wire so admin UI can show
 * "prorate_charge / refund_self_service" hints in the Billing tab).
 */
export const paymentSchema = z.object({
    id: uuidSchema,
    account_id: uuidSchema,
    subscription_id: uuidSchema.nullable(),
    amount_kop: z.number().int().nonnegative(),
    method: paymentMethodEnum,
    status: paymentStatusEnum,
    promo_code_id: uuidSchema.nullable(),
    invoice_number: z.string().nullable(),
    paid_at: isoDateTimeSchema.nullable(),
    notes: z.string().nullable(),
    created_at: isoDateTimeSchema,
});
// -------------------------------------------------------------------------
// Wave 2.20 BE-2 — admin payments surface
// (POST + GET under /admin/accounts/:accountId/payments)
// -------------------------------------------------------------------------
const INVOICE_MAX = 64;
const CURSOR_MAX = 200;
const STATUS_FILTER_MAX = 4; // length of paymentStatusEnum values
/**
 * Filter for `GET /admin/accounts/:accountId/payments` (keyset).
 *
 * `status` is REPEATABLE — admin Billing tab filters by `pending` + `paid`
 * simultaneously. Express delivers repeated query params as an array; the
 * `coerce` preprocessor lifts a single value into a single-element array so
 * `?status=paid` and `?status=paid&status=pending` parse symmetrically.
 *
 * `from` / `to` constrain the keyset's anchor column (`paid_at`). NULL
 * `paid_at` rows (pending) are unaffected by date filters — the list keeps
 * them visible regardless of `from`/`to` so admins never lose track of
 * outstanding invoices.
 */
export const paymentListFilterSchema = z
    .object({
    cursor: z.string().max(CURSOR_MAX).optional(),
    limit: z.coerce.number().int().positive().max(100).default(50),
    status: z
        .preprocess((v) => (Array.isArray(v) ? v : v === undefined ? undefined : [v]), z.array(paymentStatusEnum).max(STATUS_FILTER_MAX).optional())
        .optional(),
    method: paymentMethodEnum.optional(),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
})
    .strict();
export const paymentListResponseSchema = z.object({
    items: z.array(paymentSchema),
    next_cursor: z.string().nullable(),
});
/**
 * `POST /admin/accounts/:accountId/payments` — manual payment record
 * (admin-prd §6.5 mode A). Permitted ONLY when `account.billing_mode ===
 * 'manual'` — the service rejects with 422 BILLING_MODE_NOT_MANUAL
 * otherwise.
 *
 * `subscription_id` — the account's current subscription, the one the card
 * shows (PRD A-13, ADR-045); absent when the account has none. It must belong
 * to the account (404 SUBSCRIPTION_NOT_FOUND). The payment applies to it by
 * one rule (registration-approval arch §9.1), with `M` its monthly price
 * snapshot (`current_subscription.monthly_amount_kop`) and `n` the whole
 * monthly prices the amount covers:
 *   - a trial, running or ended, becomes paid (`active`) from the server's
 *     today for 30·n days (PRD D-22, D-25, A-16);
 *   - a paid subscription (`active`, `past_due`, `suspended`, `paused`) ends
 *     30·n days after its end, or after the server's today when that end is
 *     not later — then its period starts today (A-14). `past_due` and
 *     `suspended` become `active` and lose their past-due start (D-25: a paid
 *     client plays); `active` and `paused` keep their status;
 *   - no subscription, a cancelled or archived one (a stopped trial
 *     included), a free tariff or less than one monthly price: the payment is
 *     recorded and nothing moves (A-15). The remainder never moves anything,
 *     and the wallet is never touched (D-26).
 * A move is journalled as a child of `payment.manual_recorded`:
 * `subscription.converted_to_paid` `{ payment_id, months, period_start,
 * period_end: { from, to } }` for a trial, `subscription.extended`
 * `{ payment_id, months_added, period_end: { from, to }, counted_from:
 * 'current_end' | 'recording_day', status?: { from, to } }` for a paid
 * subscription — `status` only when a past-due or suspended one became active.
 *
 * `paid_on` is REQUIRED — the calendar day the bank says the money arrived,
 * `YYYY-MM-DD`, not after the server's today (the UTC date, arch §5.3). It is
 * stored as `paid_at` = that day 00:00 UTC, kept for accounting only: the
 * period counts from the day the payment is recorded, never from `paid_on`
 * (D-25).
 *
 * `invoice_number` is the bookkeeping reference printed on the bank
 * payment; UNIQUE per account when present (DB-level partial unique
 * index — duplicate surfaces as 409 PAYMENT_DUPLICATE_INVOICE).
 */
export const paymentCreateSchema = z
    .object({
    amount_kop: z.number().int().positive(),
    method: paymentMethodEnum,
    paid_on: isoDateSchema
        .refine(isCalendarDateIso, 'Expected a calendar date')
        .refine((day) => day <= todayUtcIso(), 'paid_on cannot be after the server’s today'),
    subscription_id: uuidSchema.optional(),
    invoice_number: z.string().trim().min(1).max(INVOICE_MAX).optional(),
})
    .strict();
/**
 * `POST /admin/accounts/:accountId/payments/:paymentId/mark-paid` — flip
 * a pending self_service payment to `paid`. The admin may override the
 * settlement timestamp; defaults to NOW(). Service rejects when the
 * source row is not `pending` (409 PAYMENT_NOT_PENDING). A row linked to a
 * subscription applies to it by the rule of {@link paymentCreateSchema},
 * counted from the server's today when it is marked paid.
 */
export const paymentMarkPaidRequestSchema = z
    .object({
    paid_at: isoDateTimeSchema.optional(),
})
    .strict()
    .refine((input) => input.paid_at === undefined || Date.parse(input.paid_at) <= Date.now(), {
    path: ['paid_at'],
    message: 'paid_at cannot be in the future',
});
/**
 * `POST /admin/accounts/:accountId/payments/:paymentId/remind` — send
 * the `payment_reminder` email to the account's billing contact. Only
 * permitted for self_service accounts on a pending row (422 otherwise).
 *
 * Empty body — the schema exists so the validation pipe rejects stray
 * fields with the canonical VALIDATION_ERROR envelope.
 */
export const paymentRemindRequestSchema = z.object({}).strict();
// -------------------------------------------------------------------------
// Wave 2.50 BE-1 (TASK-088) — billing online-mode payment-gateway interface.
//
// These schemas describe the FE/admin contract for the online-mode billing
// surface (PRD §6.5 Mode B, decisions §20). The provider abstraction lives
// in apps/api/src/payments/gateway; this file only exposes the wire DTOs so
// the admin SPA can type-check requests once Mode B is enabled.
//
// All schemas + endpoints are gated behind `BILLING_ONLINE_MODE` env flag.
// When the flag is `false` (MVP default), the API module does NOT register
// the controller and any caller receives 404. When the flag is `true` but no
// concrete provider is wired (v0.1.0 baseline), the noop provider throws
// 503 `BILLING_GATEWAY_DISABLED`.
// -------------------------------------------------------------------------
/**
 * Lifecycle status of a gateway-issued invoice.
 *   * `pending`   — invoice created, awaiting payment by the customer.
 *   * `paid`      — gateway confirmed full settlement (webhook fired).
 *   * `expired`   — invoice TTL elapsed without payment.
 *   * `cancelled` — admin or gateway cancelled the invoice.
 */
export const paymentGatewayStatusEnum = z.enum([
    'pending',
    'paid',
    'expired',
    'cancelled',
]);
const GATEWAY_DESCRIPTION_MAX = 256;
const GATEWAY_REFUND_REASON_MAX = 256;
const GATEWAY_INVOICE_ID_MAX = 128;
/**
 * `POST /admin/payments/online/invoice` — request body.
 *
 * Concrete provider rules (TTL, supported amounts) live inside each adapter;
 * the shared contract is intentionally minimal: account + amount in kopecks
 * + a short human-readable description (rendered on the gateway-hosted
 * payment page).
 */
export const paymentGatewayCreateInvoiceRequestSchema = z
    .object({
    account_id: uuidSchema,
    amount_kop: z.number().int().positive(),
    description: z.string().trim().min(1).max(GATEWAY_DESCRIPTION_MAX),
})
    .strict();
export const paymentGatewayInvoiceResponseSchema = z.object({
    invoice_id: z.string().min(1).max(GATEWAY_INVOICE_ID_MAX),
    payment_url: z.string().url(),
    expires_at: isoDateTimeSchema,
});
export const paymentGatewayStatusResponseSchema = z.object({
    invoice_id: z.string().min(1).max(GATEWAY_INVOICE_ID_MAX),
    status: paymentGatewayStatusEnum,
    paid_at: isoDateTimeSchema.nullable(),
    amount_paid_kop: z.number().int().nonnegative().nullable(),
});
/**
 * `POST /admin/payments/online/refund` — admin-initiated refund. `reason` is
 * mandatory and surfaces in the audit row + the provider's refund record.
 */
export const paymentGatewayRefundRequestSchema = z
    .object({
    invoice_id: z.string().trim().min(1).max(GATEWAY_INVOICE_ID_MAX),
    amount_kop: z.number().int().positive(),
    reason: z.string().trim().min(1).max(GATEWAY_REFUND_REASON_MAX),
})
    .strict();
export const paymentGatewayRefundResponseSchema = z.object({
    refund_id: z.string().min(1).max(GATEWAY_INVOICE_ID_MAX),
    status: z.enum(['pending', 'succeeded', 'failed']),
});
//# sourceMappingURL=payment.js.map